import { h, createContext } from "preact";
import {
  useMemo,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "preact/hooks";
import * as firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";

const DEFAULT_APP_NAME = "[DEFAULT]";

const shallowEq = (a, b) =>
  a == b ||
  [...Object.keys(a), ...Object.keys(b)].every((key) => a[key] == b[key]);

const FirebaseAppContext = createContext();

export function FirebaseAppProvider(props) {
  const { firebaseConfig, appName } = props;

  const firebaseApp = useMemo(() => {
    const existingApp = firebase.apps.find(
      (app) => app.name == (appName || DEFAULT_APP_NAME)
    );
    if (existingApp) {
      if (shallowEq(existingApp.options, firebaseConfig)) {
        return existingApp;
      }
      throw `Does not match the options already provided to the ${
        appName || "default"
      } firebase app instance, give this new instance a different appName.`;
    } else {
      return firebase.initializeApp(firebaseConfig);
    }
  }, [firebaseConfig, appName]);

  return <FirebaseAppContext.Provider value={firebaseApp} {...props} />;
}

export function useFirebaseApp() {
  const firebaseApp = useContext(FirebaseAppContext);
  if (!firebaseApp) {
    throw new Error(
      "Cannot call useFirebaseApp unless your component is within a FirebaseAppProvider"
    );
  }

  return firebaseApp;
}

export function useAuth() {
  const app = useFirebaseApp();
  return app.auth();
}

export function useFirestore() {
  const app = useFirebaseApp();
  return app.firestore();
}

export function useFirestoreCollectionData(query) {
  const [data, update] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(() => query, []);
  const ref = useMemo(() => callback(), [callback]);

  useEffect(() => {
    const unsubscribe = ref.onSnapshot((snapshot) =>
      update(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    );
    return unsubscribe;
  }, [ref]);

  return data;
}

export function useFirestoreCollectionDataWithLimit(query, limit = 5) {
  const [data, update] = useState();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(() => query, []);
  const ref = useMemo(() => callback(), [callback]);

  useEffect(() => {
    const unsubscribe = ref
      .limit(limit)
      .onSnapshot((snapshot) =>
        update(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
      );
    return unsubscribe;
  }, [ref, limit]);

  return data;
}

export function useFirestoreDocData(ref) {
  const [data, update] = useState();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(() => ref, []);
  const refMemo = useMemo(() => callback(), [callback]);

  useEffect(() => {
    const unsubscribe = refMemo.onSnapshot((snapshot) =>
      update({ id: snapshot.id, ...snapshot.data() })
    );
    return unsubscribe;
  }, [refMemo]);

  return data;
}

export function useUser() {
  const auth = useAuth();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(setUser);
    return unsubscribe;
  }, [auth]);

  return user;
}

// Auth Providers
export function FacebookAuthProvider() {
  return new firebase.auth.FacebookAuthProvider();
}

export function GoogleAuthProvider() {
  return new firebase.auth.GoogleAuthProvider();
}

export function TwitterAuthProvider() {
  return new firebase.auth.TwitterAuthProvider();
}

// Network status
export const getOnLineStatus = () =>
  typeof navigator !== "undefined" && typeof navigator.onLine === "boolean"
    ? navigator.onLine
    : true;

export function useNavigatorOnLine() {
  const [status, setStatus] = useState(getOnLineStatus());

  const setOnline = () => setStatus(true);
  const setOffline = () => setStatus(false);

  useEffect(() => {
    window.addEventListener("online", setOnline);
    window.addEventListener("offline", setOffline);

    return () => {
      window.removeEventListener("online", setOnline);
      window.removeEventListener("offline", setOffline);
    };
  }, []);

  return status;
}

// Hook for Click Outside
export const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }

      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};
